/**
 * The member's passkeys, mounted at /members/passkeys and linked from the
 * user menu: list, add, rename and remove.
 *
 * Adding and removing need a recent sign-in. When the server says so
 * (`reauth_required`), the page runs the sign-in popup again and retries.
 */
import { useToast } from "@chakra-ui/react";
import { useState } from "react";

import ConfirmModal from "components/ds/ConfirmModal";
import PasskeyEnrolment from "components/passkeys/PasskeyEnrolment";
import ReauthPrompt from "components/passkeys/ReauthPrompt";
import {
  usePasskeys,
  useRemovePasskey,
  useRenamePasskey,
  type MFPasskey,
} from "state/members/passkeys";
import {
  failureMessage,
  isReauthRequired,
  RENAME_PASSKEY_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

import PasskeysView from "./PasskeysView";

export const REMOVE_REAUTH_MESSAGE =
  "To remove a passkey, confirm it's you by signing in again.";

const Passkeys = () => {
  const toast = useToast();
  const query = usePasskeys();
  const rename = useRenamePasskey();
  const remove = useRemovePasskey();

  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState<MFPasskey | null>(null);
  // The passkey waiting on a sign-in again before it can be removed, and
  // whether the server refused it again straight after one.
  const [reauth, setReauth] = useState<{
    passkey: MFPasskey;
    stillRequired: boolean;
  } | null>(null);
  // Read once per render: the "last used" labels do not need to tick.
  const now = Date.now();

  const doRemove = (passkey: MFPasskey, afterReauth: boolean) => {
    remove.mutate(passkey.id, {
      onSuccess: () => {
        setReauth(null);
        toast({ status: "success", variant: "subtle", title: "Passkey removed" });
      },
      onError: (failure) => {
        if (isReauthRequired(failure)) {
          setReauth({ passkey, stillRequired: afterReauth });
          return;
        }
        setReauth(null);
        toast({
          status: "error",
          variant: "subtle",
          title: "Passkey not removed",
          description: failure.message,
        });
      },
    });
  };

  const onRename = async (passkey: MFPasskey, label: string) => {
    try {
      await rename.mutateAsync({ id: passkey.id, label });
    } catch (error) {
      throw new Error(failureMessage(error, RENAME_PASSKEY_FAILED_MESSAGE));
    }
  };

  return (
    <>
      <PasskeysView
        passkeys={query.data}
        isLoading={query.isLoading}
        loadError={query.isError ? query.error.message : null}
        now={now}
        removingId={remove.isLoading ? (remove.variables ?? null) : null}
        addSlot={
          adding ? (
            <PasskeyEnrolment
              submitLabel="Add passkey"
              onCreated={() => {
                setAdding(false);
                toast({ status: "success", variant: "subtle", title: "Passkey added" });
              }}
              onCancel={() => setAdding(false)}
            />
          ) : null
        }
        reauthSlot={
          reauth ? (
            <ReauthPrompt
              message={REMOVE_REAUTH_MESSAGE}
              stillRequired={reauth.stillRequired}
              onSignedIn={() => doRemove(reauth.passkey, true)}
              onCancel={() => setReauth(null)}
            />
          ) : null
        }
        onAdd={() => setAdding(true)}
        onRename={onRename}
        onRemove={(passkey) => setConfirming(passkey)}
      />

      {confirming && (
        <ConfirmModal
          title="Remove this passkey?"
          body={`You will not be able to sign with ${confirming.label} any more. If it is your only passkey, you will need to add another before you can sign.`}
          confirmLabel="Remove passkey"
          cancelLabel="Keep passkey"
          confirmTone="danger"
          onConfirm={() => {
            const target = confirming;
            setConfirming(null);
            doRemove(target, false);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </>
  );
};

export default Passkeys;
